import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { PencilSimple, Camera, ShieldCheck, ShieldWarning, Wallet, ArrowDown } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_AVATAR = "https://images.pexels.com/photos/5906433/pexels-photo-5906433.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', enabled: true }, { code: 'USD', symbol: '$', enabled: false }, { code: 'EUR', symbol: '\u20AC', enabled: false },
  { code: 'GBP', symbol: '\u00A3', enabled: false }, { code: 'AED', symbol: 'AED', enabled: false }, { code: 'SGD', symbol: 'S$', enabled: false },
  { code: 'AUD', symbol: 'A$', enabled: false }, { code: 'JPY', symbol: '\u00A5', enabled: false },
];

export default function ProfileCard({ user, onUpdate, unitsBalance }) {
  const { theme } = useTheme();
  const { refreshUser } = useAuth();
  const isDark = theme === 'dark';
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

  // Fetch profile photo as blob when user has a stored photo path
  useEffect(() => {
    if (user?.profile_photo_path) {
      const fetchPhoto = async () => {
        try {
          const response = await axios.get(`${API}/files/${user.profile_photo_path}`, {
            withCredentials: true,
            responseType: 'blob'
          });
          const blobUrl = URL.createObjectURL(response.data);
          setProfilePhotoUrl(blobUrl);
        } catch (err) {
          console.error('Failed to fetch profile photo:', err);
          setProfilePhotoUrl(null);
        }
      };
      fetchPhoto();
      return () => {
        if (profilePhotoUrl) URL.revokeObjectURL(profilePhotoUrl);
      };
    } else {
      setProfilePhotoUrl(null);
    }
  }, [user?.profile_photo_path]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProfilePhotoUrl = () => {
    if (profilePhotoUrl) return profilePhotoUrl;
    return user?.picture || DEFAULT_AVATAR;
  };

  const handleEditStart = (field, value) => {
    setEditing(field);
    setEditValue(value || '');
  };

  const handleEditSave = async (field) => {
    try {
      await axios.put(`${API}/profile`, { [field]: editValue }, { withCredentials: true });
      await refreshUser();
      setEditing(null);
      toast.success(`${field} updated`);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/profile/upload-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
      toast.success('Photo updated');
    } catch (err) {
      toast.error('Failed to upload photo');
    }
  };

  return (
    <div 
      className={`border p-6 ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'} rounded-md`}
      data-testid="profile-card"
    >
      {/* Photo */}
      <div className="flex justify-center mb-5">
        <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          <div className={`w-20 h-20 rounded-full overflow-hidden border-2 ${isDark ? 'border-zinc-700' : 'border-zinc-300'}`}>
            <img 
              src={getProfilePhotoUrl()} 
              alt={user?.name} 
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
              data-testid="profile-photo"
            />
          </div>
          <div className="absolute bottom-0 right-0 w-7 h-7 bg-yellow-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera size={12} weight="bold" className="text-zinc-950" />
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="profile-photo-input" />
        </div>
      </div>

      {/* Name */}
      <div className="mb-4">
        <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Name
        </label>
        <div className="flex items-center gap-2 mt-1">
          {editing === 'name' ? (
            <>
              <Input
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                className={`h-8 text-sm rounded-sm font-ibm flex-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`}
                onKeyDown={e => e.key === 'Enter' && handleEditSave('name')}
                autoFocus
                data-testid="edit-name-input"
              />
              <button onClick={() => handleEditSave('name')} className="text-yellow-400 text-xs font-ibm font-semibold" data-testid="save-name-button">Save</button>
            </>
          ) : (
            <>
              <span className={`font-ibm text-sm font-medium flex-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`} data-testid="profile-name">{user?.name}</span>
              <button onClick={() => handleEditStart('name', user?.name)} className={`${isDark ? 'text-zinc-500 hover:text-yellow-400' : 'text-zinc-400 hover:text-yellow-500'} transition-colors`} data-testid="edit-name-button">
                <PencilSimple size={14} weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="mb-4">
        <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Email
        </label>
        <p className={`font-ibm text-sm mt-1 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`} data-testid="profile-email">{user?.email}</p>
      </div>

      {/* Phone */}
      <div className="mb-4">
        <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Phone
        </label>
        <div className="flex items-center gap-2 mt-1">
          <span className={`font-ibm text-sm flex-1 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`} data-testid="profile-phone">
            {user?.phone || 'Not set'}
          </span>
        </div>
      </div>

      {/* Currency */}
      <div className="mb-5">
        <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Currency
        </label>
        <div className="flex items-center gap-2 mt-1">
          {editing === 'currency' ? (
            <Select value={editValue} onValueChange={(val) => { setEditValue(val); }}>
              <SelectTrigger className={`h-8 text-sm rounded-sm font-ibm flex-1 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-white border-zinc-200 text-zinc-950'}`} data-testid="edit-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.code} value={c.code} disabled={!c.enabled} className={`font-ibm text-sm ${!c.enabled ? 'opacity-40' : ''}`}>{c.symbol} {c.code}{!c.enabled ? ' (Coming soon)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <>
              <span className={`font-ibm text-sm font-medium flex-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`} data-testid="profile-currency">
                {CURRENCIES.find(c => c.code === user?.currency)?.symbol} {user?.currency}
              </span>
              <button onClick={() => handleEditStart('currency', user?.currency)} className={`${isDark ? 'text-zinc-500 hover:text-yellow-400' : 'text-zinc-400 hover:text-yellow-500'} transition-colors`} data-testid="edit-currency-button">
                <PencilSimple size={14} weight="bold" />
              </button>
            </>
          )}
          {editing === 'currency' && (
            <button onClick={() => handleEditSave('currency')} className="text-yellow-400 text-xs font-ibm font-semibold" data-testid="save-currency-button">Save</button>
          )}
        </div>
      </div>

      {/* Balance */}
      <div className={`p-3 border rounded-sm mb-4 ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="units-balance-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet size={16} weight="bold" className="text-yellow-400" />
            <div>
              <span className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Balance</span>
              <p className={`font-outfit text-lg font-bold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`} data-testid="profile-units-balance">
                {'\u20B9'}{(unitsBalance || 0).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (!user?.bank_verified) {
                navigate('/kyc');
              } else if ((unitsBalance || 0) > 0) {
                toast.info('Payouts via RazorpayX will be enabled shortly. Your balance is safe.');
              } else {
                toast.info('No balance to withdraw yet. Funds arrive when a cycle pays you.');
              }
            }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-sm border font-ibm text-[10px] font-semibold uppercase tracking-wider transition-all active:scale-[0.98] ${isDark ? 'border-zinc-700 text-zinc-400 hover:border-yellow-400/50 hover:text-yellow-400' : 'border-zinc-200 text-zinc-600 hover:border-yellow-400/50 hover:text-yellow-600'}`}
            data-testid="withdraw-button"
          >
            <ArrowDown size={12} weight="bold" />
            Withdraw
          </button>
        </div>
      </div>

      {/* KYC Status */}
      <div className={`p-3 border rounded-sm ${user?.bank_verified
        ? (isDark ? 'border-green-800 bg-green-500/10' : 'border-green-200 bg-green-50')
        : (isDark ? 'border-yellow-800/40 bg-yellow-400/5' : 'border-yellow-200 bg-yellow-50')
      }`} data-testid="verification-status">
        <div className="flex items-center gap-2">
          {user?.bank_verified ? (
            <>
              <ShieldCheck size={18} weight="bold" className="text-green-500" />
              <span className={`font-ibm text-xs font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>KYC Verified</span>
            </>
          ) : (
            <>
              <ShieldWarning size={18} weight="bold" className="text-yellow-400" />
              <span className={`font-ibm text-xs font-medium flex-1 ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>KYC Pending</span>
              <button
                className="font-ibm text-[10px] font-semibold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-3 py-1 rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all"
                data-testid="verify-documents-button"
                onClick={() => navigate('/kyc')}
              >
                Verify
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
